// Comisiones PM sobre pedidos de cliente (CEO, 22/10/2026).
//
// Reglas:
//  - Solo pedidos CONFIRMADOS (de Aceptado en adelante). Cotizaciones,
//    pendientes de respuesta, rechazados y cancelados no generan comisión.
//  - La comisión se DEVENGA en la fecha de entrega:
//      · pedido marcado ENTREGADO → la fecha que se dio al marcarlo
//        (pedidos_cliente.fecha_entrega), para todas sus líneas;
//      · si no la hay (histórico, entregas parciales) → la fecha de cada
//        entrega registrada, por las unidades de esa entrega;
//      · pedido cerrado sin entregas registradas → la fecha de entrega real
//        de la línea, si la tiene.
//  - Lo que queda por entregar se ESTIMA en la ETA de la línea o, si no hay,
//    en la fecha de entrega solicitada por el cliente.
//  - La base es el importe de la línea NETO del descuento técnico
//    (dto_tecnico_pct, server/lib/dto-tecnico-db.ts): se descuenta en cada
//    factura antes de calcular rappel y comisión (CEO, 26/10/2026).
//  - % = comision_pm_pct de la condición comercial más específica del
//    producto (marca › gama › categoría › subcategoría), la misma regla que la
//    tarjeta «Rappel / Comisión / Provisión» de la ficha del pedido.

export const ESTADOS_CONFIRMADOS = [
  'ACEPTADO', 'EN_PRODUCCION', 'EN_CAMINO', 'INCIDENCIA', 'ENTREGA_PARCIAL', 'ENTREGADO', 'FINALIZADO',
] as const;
const CERRADOS = new Set(['ENTREGADO', 'FINALIZADO']);

export type LineaComision = {
  linea_id: string;
  pedido_id: string;
  pedido_estado: string;
  /** Fecha dada al marcar el pedido ENTREGADO (YYYY-MM-DD). */
  pedido_fecha_entrega: string | null;
  unidades: number;
  unidades_entregadas: number;
  precio_unitario: number | null;
  eta: string | null;
  fecha_solicitada: string | null;
  fecha_entrega_real: string | null;
  entregas: { fecha: string; unidades: number }[];
  /** % de comisión (3.5 = 3,5 %); null si ninguna condición aplica. */
  comision_pct: number | null;
  /** Descuento técnico de la línea (2 = 2 %); se resta de la base. Sin dato = 0. */
  dto_tecnico_pct?: number | null;
};

export type OrigenFecha = 'entregado' | 'entrega' | 'entrega_real' | 'eta' | 'solicitada' | 'sin_fecha';

export type MovimientoComision = {
  linea_id: string;
  pedido_id: string;
  tipo: 'devengada' | 'estimada';
  fecha: string | null;
  origen: OrigenFecha;
  unidades: number;
  /** Importe antes del descuento técnico. */
  base_bruta_eur: number;
  dto_tecnico_eur: number;
  /** Base de la comisión: importe neto del descuento técnico. */
  base_eur: number;
  comision_pct: number | null;
  comision_eur: number;
  /** Días entre la entrega y lo solicitado / la ETA (+ = tarde). Solo devengadas. */
  dias_vs_solicitada: number | null;
  dias_vs_eta: number | null;
};

export function diasEntre(desde: string | null, hasta: string | null): number | null {
  if (!desde || !hasta) return null;
  const a = Date.parse(`${desde.slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${hasta.slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round((b - a) / 86_400_000);
}

const redondear = (n: number) => Math.round(n * 100) / 100;

/** Reparte una línea de pedido en comisión devengada (por fecha de entrega) y estimada. */
export function movimientosDeLinea(l: LineaComision): MovimientoComision[] {
  if (!(ESTADOS_CONFIRMADOS as readonly string[]).includes(l.pedido_estado)) return [];
  const precio = Number(l.precio_unitario) || 0;
  const pct = l.comision_pct == null ? null : Number(l.comision_pct);
  const dto = Number(l.dto_tecnico_pct) || 0;
  const mov = (tipo: MovimientoComision['tipo'], fecha: string | null, origen: OrigenFecha, unidades: number): MovimientoComision => {
    const bruta = redondear(precio * unidades);
    const base = redondear(bruta * (1 - dto / 100));
    return {
      linea_id: l.linea_id,
      pedido_id: l.pedido_id,
      tipo,
      fecha: fecha ? fecha.slice(0, 10) : null,
      origen,
      unidades,
      base_bruta_eur: bruta,
      dto_tecnico_eur: redondear(bruta - base),
      base_eur: base,
      comision_pct: pct,
      comision_eur: redondear(base * ((pct ?? 0) / 100)),
      dias_vs_solicitada: tipo === 'devengada' ? diasEntre(l.fecha_solicitada, fecha) : null,
      dias_vs_eta: tipo === 'devengada' ? diasEntre(l.eta, fecha) : null,
    };
  };

  const unidades = Math.max(0, Number(l.unidades) || 0);
  const entregadas = Math.max(0, Number(l.unidades_entregadas) || 0);

  // Marcado ENTREGADO con fecha: esa fecha manda para todo el pedido.
  if (l.pedido_fecha_entrega) {
    const uds = Math.max(unidades, entregadas);
    return uds > 0 ? [mov('devengada', l.pedido_fecha_entrega, 'entregado', uds)] : [];
  }

  const out: MovimientoComision[] = [];
  const entregas = l.entregas.filter((e) => Number(e.unidades) > 0);
  for (const e of entregas) out.push(mov('devengada', e.fecha, 'entrega', Number(e.unidades)));

  if (CERRADOS.has(l.pedido_estado)) {
    // Pedido cerrado sin entregas registradas: la línea entera en su fecha
    // de entrega real (o sin fecha, para que se vea y se corrija).
    if (entregas.length === 0 && unidades > 0) {
      out.push(l.fecha_entrega_real
        ? mov('devengada', l.fecha_entrega_real, 'entrega_real', unidades)
        : mov('devengada', null, 'sin_fecha', unidades));
    }
    return out;
  }

  const pendientes = Math.max(unidades - entregadas, 0);
  if (pendientes > 0) {
    if (l.eta) out.push(mov('estimada', l.eta, 'eta', pendientes));
    else if (l.fecha_solicitada) out.push(mov('estimada', l.fecha_solicitada, 'solicitada', pendientes));
    else out.push(mov('estimada', null, 'sin_fecha', pendientes));
  }
  return out;
}

export type ResumenComision = {
  devengada_eur: number;
  devengada_base: number;
  estimada_eur: number;
  estimada_base: number;
  pedidos: number;
};

/** Suma movimientos (devengada / estimada) y cuenta pedidos distintos. */
export function resumir(movs: Pick<MovimientoComision, 'tipo' | 'comision_eur' | 'base_eur' | 'pedido_id'>[]): ResumenComision {
  const r = { devengada_eur: 0, devengada_base: 0, estimada_eur: 0, estimada_base: 0, pedidos: 0 };
  const pedidos = new Set<string>();
  for (const m of movs) {
    pedidos.add(m.pedido_id);
    if (m.tipo === 'devengada') { r.devengada_eur += m.comision_eur; r.devengada_base += m.base_eur; }
    else { r.estimada_eur += m.comision_eur; r.estimada_base += m.base_eur; }
  }
  return {
    devengada_eur: redondear(r.devengada_eur),
    devengada_base: redondear(r.devengada_base),
    estimada_eur: redondear(r.estimada_eur),
    estimada_base: redondear(r.estimada_base),
    pedidos: pedidos.size,
  };
}
